import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { MessageModel } from '../types/models';
import { useTTSStore } from '../stores/ttsStore';

// Type definitions
interface MenuPosition {
  x: number;
  y: number;
}

interface MenuItem {
  id: string;
  label: string;
  icon?: string;
  action?: () => void;
  submenu?: MenuItem[];
  condition?: () => boolean;
  divider?: boolean;
}

interface MessageMenuProps {
  chatInputRef: React.RefObject<any>;
  additionalMenuItems?: MenuItem[];
}

interface FormModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

function FormModal({ isOpen, onClose, title, children }: FormModalProps) {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-neutral-600 bg-opacity-50 flex items-center justify-center z-[10000]">
      <div className="bg-neutral-900 rounded-lg p-5 w-[500px] max-w-[90vw] shadow-lg shadow-black/50">
        <div className="flex justify-between items-center mb-4 pb-2 border-b border-neutral-800">
          <h3 className="text-lg font-medium text-red-400">{title}</h3>
          <button
            className="text-neutral-400 hover:text-red-400 transition-colors duration-200"
            onClick={onClose}
            aria-label='Close modal'
          >
            <i className="fas fa-times" />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body
  );
};


export function MessageMenu({
  chatInputRef,
  additionalMenuItems = []
}: MessageMenuProps) {
  const [menuState, setMenuState] = useState<{
    isOpen: boolean;
    position: MenuPosition | null;
    selectedText: string;
  }>({
    isOpen: false,
    position: null,
    selectedText: ''
  });

  const [submenuState, setSubmenuState] = useState<{
    isOpen: boolean;
    parentId: string | null;
  }>({
    isOpen: false,
    parentId: null
  });

  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [formData, setFormData] = useState<any>({});
  
  const tts = useTTSStore();
  const menuRef = useRef<HTMLDivElement>(null);
  const submenuRef = useRef<HTMLDivElement>(null);

  // Helper functions
  const getSelectedText = useCallback(() => {
    return window.getSelection()?.toString() || '';
  }, []);

  const isInSelectedText = useCallback((e: MouseEvent): boolean => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.toString().length === 0) {
      return false;
    }

    for (let i = 0; i < selection.rangeCount; i++) {
      const range = selection.getRangeAt(i);
      const rects = range.getClientRects();
      
      for (let j = 0; j < rects.length; j++) {
        const rect = rects[j];
        if (e.clientX >= rect.left && e.clientX <= rect.right && 
            e.clientY >= rect.top && e.clientY <= rect.bottom) {
          return true;
        }
      }
    }
    return false;
  }, []);

  const closeMenu = useCallback((keepSelectedText = true) => {
    setMenuState(prev => ({
      isOpen: false, 
      position: null, 
      selectedText: keepSelectedText ? prev.selectedText : ''
    }));
    setSubmenuState({ isOpen: false, parentId: null });
  }, []);

  // Action handlers
  const handleExplain = useCallback(() => {
    
    if (chatInputRef.current && menuState.selectedText) {
      chatInputRef.current.setValue(`Can you explain "${menuState.selectedText}"?`);
      chatInputRef.current.submitMessage();
    }
    closeMenu();
  }, [menuState.selectedText, chatInputRef, closeMenu]);

  const handleCopy = useCallback(() => {
    if (menuState.selectedText) {
      navigator.clipboard.writeText(menuState.selectedText)
        .catch(err => console.error('Failed to copy text:', err));
    }
    closeMenu();
  }, [menuState.selectedText, closeMenu]);

  const handleTranslate = useCallback((language: string) => {
    if (chatInputRef.current && menuState.selectedText) {
      const prompt = `Translate the following text to ${language}, and show the translation in a table response format:\n\n"${menuState.selectedText}"`;
      chatInputRef.current.setValue(prompt);
      chatInputRef.current.submitMessage();
    }
    setActiveModal(null);
    setMenuState({ isOpen: false, position: null, selectedText: '' }); // Full reset after form submission

  }, [menuState.selectedText, chatInputRef]);

  const handleTTS = useCallback((language: string) => {
    const message: MessageModel = {
      content: menuState.selectedText,
      role: 'user',
    };
    
    // Specify 'selection' as the source
    tts.play(message, true, language, 'text-selection');
    
    setActiveModal(null);
    setMenuState({ isOpen: false, position: null, selectedText: '' });
  }, [menuState.selectedText, tts]);

  const isInChatInput = useCallback((position?: MenuPosition | null): boolean => {
    if (!chatInputRef.current?.textareaRef.current || !position) return false;
    
    const textareaElement = chatInputRef.current.textareaRef.current;
    const textareaRect = textareaElement.getBoundingClientRect();
    
    return (
      position.x >= textareaRect.left && 
      position.x <= textareaRect.right && 
      position.y >= textareaRect.top && 
      position.y <= textareaRect.bottom
    );
  }, [chatInputRef]);

  const handlePaste = useCallback(async () => {
    try {
      const clipboardText = await navigator.clipboard.readText();
      if (chatInputRef.current && clipboardText) {
        const textarea = chatInputRef.current.textareaRef.current;
        if (textarea) {
          const startPos = textarea.selectionStart || 0;
          const endPos = textarea.selectionEnd || 0;
          
          const currentValue = textarea.value;
          const newValue = 
            currentValue.substring(0, startPos) + 
            clipboardText + 
            currentValue.substring(endPos);
          
          textarea.value = newValue;
          
          const newCursorPos = startPos + clipboardText.length;
          textarea.setSelectionRange(newCursorPos, newCursorPos);
          
          const inputEvent = new Event('input', { bubbles: true });
          textarea.dispatchEvent(inputEvent);
          
          chatInputRef.current.setValue(newValue);
        }
      }
    } catch (err) {
      console.error('Failed to paste text:', err);
    }
    closeMenu();
  }, [chatInputRef, closeMenu]);

  // Menu configuration
  const menuItems = useMemo<MenuItem[]>(() => {
    const items: MenuItem[] = [
      {
        id: 'explain',
        label: 'Explain',
        icon: 'fas fa-lightbulb',
        action: handleExplain,
        condition: () => menuState.selectedText.length > 0
      },
      {
        id: 'copy',
        label: 'Copy',
        icon: 'fas fa-copy',
        action: handleCopy,
        condition: () => menuState.selectedText.length > 0
      },
      {
        id: 'paste',
        label: 'Paste',
        icon: 'fas fa-paste',
        action: handlePaste,
        condition: () => isInChatInput(menuState.position) && !menuState.selectedText.length
      },
      {
        id: 'actions',
        label: 'Actions',
        icon: 'fa-solid fa-ellipsis',
        condition: () => menuState.selectedText.length > 0,
        submenu: [
          {
            id: 'translate',
            label: 'Translate',
            icon: 'fas fa-language',
            action: () => {
              setActiveModal('translate')
            }
          },
          {
            id: 'tts',
            label: 'Text to Speech',
            icon: 'fas fa-volume-up',
            action: () => {
              handleTTS('en-us');
            }
          }
        ]
      }
    ];

    return [...items, ...additionalMenuItems];
  }, [menuState.selectedText, menuState.position, handleExplain, handleCopy, handlePaste, handleTTS, isInChatInput, additionalMenuItems]);

  // Calculate menu position to keep it in viewport
  const calculateMenuPosition = useCallback((x: number, y: number, menuElement: HTMLElement | null) => {
    if (!menuElement) return { x, y };

    const menuWidth = menuElement.offsetWidth || 200;
    const menuHeight = menuElement.offsetHeight || 100;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let adjustedX = x;
    let adjustedY = y;

    if (x + menuWidth > viewportWidth - 20) {
      adjustedX = x - menuWidth;
    }

    if (y + menuHeight > viewportHeight - 20) {
      adjustedY = viewportHeight - menuHeight - 20;
    }

    if (adjustedY < 20) {
      adjustedY = 20;
    }

    return { x: adjustedX, y: adjustedY };
  }, []);

  // Calculate submenu position to keep it in viewport
  const calculateSubmenuPosition = useCallback((parentElement: HTMLElement | null, submenuElement: HTMLElement | null) => {
    if (!parentElement || !menuState.position) {
      return { x: 0, y: 0 };
    }

    const parentRect = parentElement.getBoundingClientRect();
    const submenuWidth = submenuElement?.offsetWidth || 200;
    const submenuHeight = submenuElement?.offsetHeight || 150;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    const activeMenuItem = parentElement.querySelector(`[data-menu-id="${submenuState.parentId}"]`);
    const itemRect = activeMenuItem?.getBoundingClientRect();
    
    let x = parentRect.right + 1; 
    let y = itemRect?.top || parentRect.top;
    
    // Check if submenu would go off right edge
    if (x + submenuWidth > viewportWidth - 10) {
      
      x = parentRect.left - submenuWidth + 45;
      
      // If positioning to the left would go off screen, overlap slightly with parent
      if (x < 10) {
        x = 15;
      }
    }
    
    // Check if submenu would go off bottom edge
    if (y + submenuHeight > viewportHeight - 10) {
      y = viewportHeight - submenuHeight - 10;
    }
    
    // Check if submenu would go off top edge
    if (y < 10) {
      y = 10;
    }
    
    return { x, y };
  }, [menuState.position, submenuState.parentId]);

  // Event handlers
  useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      
      const selectedText = getSelectedText().trim();
      const previousText = menuState.selectedText;
      
      
      const textToUse = menuState.isOpen && !selectedText ? previousText : selectedText;
  
      if ((textToUse && isInSelectedText(e)) || isInChatInput({ x: e.clientX, y: e.clientY })) {
        setMenuState({
          isOpen: true,
          position: { x: e.clientX, y: e.clientY },
          selectedText: textToUse
        });
        // Reset submenu state when opening the main menu
        setSubmenuState({ isOpen: false, parentId: null });
      } else {
        closeMenu();
      }
    };

    const handleClickOutside = (e: MouseEvent) => {

      if (activeModal) return;

      setTimeout(() => {
        const clickedOnMainMenu = menuRef.current && menuRef.current.contains(e.target as Node);
        const clickedOnSubmenu = submenuRef.current && submenuRef.current.contains(e.target as Node);
        
        // If menu is open and click is outside both menus, close everything
        if (menuState.isOpen && !clickedOnMainMenu && !clickedOnSubmenu) {
          closeMenu();
        } 
        // If only submenu is open and click is outside submenu but inside main menu, close submenu only
        else if (submenuState.isOpen && !clickedOnSubmenu && clickedOnMainMenu) {
          setSubmenuState({ isOpen: false, parentId: null });
        }
      }, 0);
    };

    const handleScroll = () => {
      if (menuState.isOpen) {
        closeMenu(true);
      }
    };
    const handleMouseDown = (e: MouseEvent) => {
      // Skip right clicks (context menu)
      if (e.button === 2) return;
      
      // Don't process if modals are open
      if (activeModal) return;

      const clickedOnMainMenu = menuRef.current && menuRef.current.contains(e.target as Node);
      const clickedOnSubmenu = submenuRef.current && submenuRef.current.contains(e.target as Node);
      
      // Close menu if clicking outside both menus
      if (menuState.isOpen && !clickedOnMainMenu && !clickedOnSubmenu) {
        e.preventDefault(); // Prevent text selection when closing menu
        closeMenu();
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('scroll', handleScroll, true);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [menuState.isOpen, submenuState.isOpen, closeMenu, activeModal, getSelectedText, isInSelectedText, isInChatInput, menuState.selectedText]);


  const renderMenuItem = (item: MenuItem) => {
    if (item.divider) {
      return <div key={item.id} className="border-t border-neutral-500 my-1" />;
    }

    if (item.condition && !item.condition()) {
      return null;
    }

    const hasSubmenu = item.submenu && item.submenu.length > 0;

    return (
      <button
        key={item.id}
        data-menu-id={item.id}
        className="flex p-2 px-3 w-full border-0 bg-neutral-700 cursor-pointer text-left text-neutral-50 hover:bg-red-600 transition-colors duration-150"
        onClick={(e) => {
          e.stopPropagation();
          
          if (hasSubmenu) {
            // Toggle submenu on click for items with submenus
            setSubmenuState(prev => {
              if (prev.isOpen && prev.parentId === item.id) {
                return { isOpen: false, parentId: null };
              } 
                return { isOpen: true, parentId: item.id };
              
            });
          } else if (item.action) {
            item.action();
          }
        }}
      >
        <div className="flex justify-between items-center w-full">
          <span className="flex items-center">
            {item.icon && (
              <span className={`mr-4 ${item.icon} text-red-400 text-sm w-5`} />
            )}
            <span className="text-sm">{item.label}</span>
          </span>
          {hasSubmenu && (
            <i className="fas fa-chevron-right text-xs text-neutral-400 ml-4" />
          )}
        </div>
      </button>
    );
  };

  const visibleMenuItems = menuItems.filter(item => 
    !item.condition || item.condition()
  );

  const closeModal = useCallback(() => {
    setActiveModal(null);
    setFormData({});
  }, []);

  // Get adjusted position after render
  useEffect(() => {
    if (menuState.isOpen && menuState.position && menuRef.current) {
      const adjusted = calculateMenuPosition(
        menuState.position.x,
        menuState.position.y,
        menuRef.current
      );
      
      if (adjusted.x !== menuState.position.x || adjusted.y !== menuState.position.y) {
        setMenuState(prev => ({ ...prev, position: adjusted }));
      }
    }
  }, [menuState.isOpen, menuState.position, calculateMenuPosition]);

  // Add state for submenu position
  const [submenuPosition, setSubmenuPosition] = useState<MenuPosition | null>(null);

  // Calculate submenu position when it opens
  useEffect(() => {
    if (submenuState.isOpen && submenuState.parentId && menuRef.current) {
      
      requestAnimationFrame(() => {
        const position = calculateSubmenuPosition(menuRef.current, submenuRef.current);
        setSubmenuPosition(position);
      });
    } else {
      setSubmenuPosition(null);
    }
  }, [submenuState.isOpen, submenuState.parentId, calculateSubmenuPosition]);

  return (
    <>
      {/* Main Context Menu */}
      {menuState.isOpen && menuState.position && ReactDOM.createPortal(
        <div
          ref={menuRef}
          className="context-menu bg-neutral-700 border border-neutral-400 text-left overflow-hidden rounded-md shadow-md"
          style={{
            position: 'fixed',
            left: `${menuState.position.x}px`,
            top: `${menuState.position.y}px`,
            zIndex: 9999
          }}
        >
          {visibleMenuItems.map(renderMenuItem)}
        </div>,
        document.body
      )}

      {/* Submenu */}
      {menuState.isOpen && submenuState.isOpen && submenuState.parentId && submenuPosition && ReactDOM.createPortal(
        <div
          ref={submenuRef}
          className="context-menu bg-neutral-700 border border-neutral-400 text-left overflow-hidden rounded-md shadow-md"
          style={{
            position: 'fixed',
            left: `${submenuPosition.x}px`,
            top: `${submenuPosition.y}px`,
            zIndex: 10000,
            opacity: submenuPosition ? 1 : 0, // Prevent flash
            transition: 'opacity 0.1s ease-in'
          }}
        >
          {menuItems
            .find(item => item.id === submenuState.parentId)
            ?.submenu?.map(renderMenuItem)}
        </div>,
        document.body
      )}

      {/* Translation Modal */}
      <FormModal
        isOpen={activeModal === 'translate'}
        onClose={closeModal}
        title="Translate"
      >
        <form onSubmit={(e) => {
          e.preventDefault();
          handleTranslate(formData.language || 'Spanish');
        }}>
          <div className="space-y-4">
            <div>
              <span className="text-sm text-red-400 mb-1 font-medium">Selected Text:</span>
              <div className="bg-black p-2 rounded border border-neutral-800 text-sm text-neutral-200 max-h-[120px] overflow-y-auto">
                {menuState.selectedText}
              </div>
            </div>
            <div>
              <span className="block text-sm text-red-400 mb-1 font-medium">Translate to:</span>
              <select
                className="w-full p-2 bg-black border border-red-800 rounded text-white focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
                value={formData.language || 'Spanish'}
                onChange={(e) => setFormData({ ...formData, language: e.target.value })}
              >
                <option value="Arabic">Arabic</option>
                <option value="Bengali">Bengali</option>
                <option value="Chinese">Chinese</option>
                <option value="English">English</option>
                <option value="French">French</option>
                <option value="German">German</option>
                <option value="Hindi">Hindi</option>
                <option value="Indonesian">Indonesian</option>
                <option value="Italian">Italian</option>
                <option value="Japanese">Japanese</option>
                <option value="Korean">Korean</option>
                <option value="Malay">Malay</option>
                <option value="Nepali">Nepali</option>
                <option value="Persian">Persian</option>
                <option value="Polish">Polish</option>
                <option value="Portuguese">Portuguese</option>
                <option value="Romanian">Romanian</option>
                <option value="Russian">Russian</option>
                <option value="Serbian">Serbian</option>
                <option value="Spanish">Spanish</option>
                <option value="Swedish">Swedish</option>
                <option value="Turkish">Turkish</option>
                <option value="Ukrainian">Ukrainian</option>
                <option value="Vietnamese">Vietnamese</option>
              </select>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                className="px-4 py-2 bg-neutral-900 text-neutral-300 rounded border border-neutral-700 hover:bg-neutral-800"
                onClick={() => setActiveModal(null)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-5 py-2 bg-red-700 text-white rounded border border-red-600 hover:bg-red-600"
              >
                Translate
              </button>
            </div>
          </div>
        </form>
      </FormModal>
    </>
  );
};

export default MessageMenu;